import React, { useState, useMemo } from 'react';
import { Card, Button, Text, Caption } from '../../design-system';
import DriftFindingRow from './DriftFindingRow';

const T = {
  hand: '"Caveat", "Nunito", cursive',
  sans: '"Nunito", ui-sans-serif, system-ui, -apple-system, sans-serif',
  ink: '#2E2109',
  ink2: '#4A3510',
  inkMuted: '#6E5A38',
  orange: '#EA7C1F',
};

function fuzzyTitleMatch(a, b) {
  const norm = (s) => (s || '').toLowerCase().replace(/\s+/g, ' ').trim();
  return norm(a) === norm(b);
}

function computeResolved(prev, current) {
  // For each repo present in prev, find findings not present in current
  // (matched by file_path + title fuzzy).
  const reposInPrev = [...new Set(prev.map(f => f.repo))];
  const resolved = [];
  for (const r of reposInPrev) {
    const prevR = prev.filter(f => f.repo === r);
    const currR = current.filter(f => f.repo === r);
    for (const p of prevR) {
      const stillPresent = currR.some(
        c => c.file_path === p.file_path && fuzzyTitleMatch(c.title, p.title)
      );
      if (!stillPresent) resolved.push(p);
    }
  }
  return resolved;
}

export default function DriftInbox({
  findings,
  previousFindings,
  dismissed = [],
  repoMeta,
  githubRepos,
  onUpdateSpec,
  updateSpecRunning,
  onDismiss,
  onUndismiss,
  dismissRunning,
  onScan,
  scanning,
}) {
  const [directionFilter, setDirectionFilter] = useState('all');
  const [repoFilter, setRepoFilter] = useState('');
  const [resolvedExpanded, setResolvedExpanded] = useState(false);
  const [dismissedExpanded, setDismissedExpanded] = useState(false);

  const counts = useMemo(() => {
    const by = { all: 0, code_ahead: 0, spec_ahead: 0, high: 0, medium: 0, low: 0 };
    for (const f of findings) {
      by.all++;
      if (by[f.direction] !== undefined) by[f.direction]++;
      if (by[f.severity] !== undefined) by[f.severity]++;
    }
    return by;
  }, [findings]);

  const repoOptions = useMemo(
    () => [...new Set(findings.map(f => f.repo))],
    [findings]
  );

  const visible = useMemo(() => findings.filter(f => {
    if (directionFilter !== 'all' && f.direction !== directionFilter) return false;
    if (repoFilter && f.repo !== repoFilter) return false;
    return true;
  }), [findings, directionFilter, repoFilter]);

  const resolved = useMemo(
    () => computeResolved(previousFindings || [], findings || []),
    [previousFindings, findings]
  );

  const showRepoBadge = (githubRepos?.length || 0) >= 2;

  const FilterTab = ({ value, label, count }) => {
    if (value !== 'all' && count === 0) return null;
    const active = directionFilter === value;
    return (
      <button
        type="button"
        onClick={() => setDirectionFilter(value)}
        style={{
          fontFamily: T.sans,
          fontSize: 13,
          fontWeight: 600,
          padding: '5px 12px',
          borderRadius: 999,
          border: active ? '1px solid transparent' : '1px solid var(--color-border)',
          background: active ? T.ink : 'transparent',
          color: active ? '#FDF7E5' : T.ink2,
          cursor: 'pointer',
          letterSpacing: '0.01em',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        {active && value === 'all' && <span style={{ fontSize: 10 }}>◆</span>}
        <span>{label}</span>
        <span style={{ opacity: active ? 0.7 : 0.55 }}>{count}</span>
      </button>
    );
  };

  const hasFindings = counts.all > 0;

  return (
    <Card>
      <div style={{ padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{
              fontFamily: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
              fontSize: 11, fontWeight: 800, letterSpacing: '0.6px',
              textTransform: 'uppercase', color: T.inkMuted, marginBottom: 6,
            }}>
              Drift inbox
            </div>
            {hasFindings ? (
              <div style={{ fontFamily: T.sans, fontSize: 18, color: T.ink, fontWeight: 600 }}>
                <span>{counts.all}</span>
                <span style={{ fontWeight: 400, color: T.inkMuted }}> open</span>
                {counts.high > 0 && <>
                  <span style={{ color: T.inkMuted, fontWeight: 400 }}> · </span>
                  <span style={{ color: '#C14A33', fontWeight: 600 }}>● </span>
                  <span style={{ color: '#C14A33', fontWeight: 600 }}>{counts.high} high</span>
                </>}
                {counts.medium > 0 && <>
                  <span style={{ color: T.inkMuted, fontWeight: 400 }}> · </span>
                  <span style={{ color: '#EA7C1F', fontWeight: 600 }}>● </span>
                  <span style={{ color: '#EA7C1F', fontWeight: 600 }}>{counts.medium} medium</span>
                </>}
                {counts.low > 0 && <>
                  <span style={{ color: T.inkMuted, fontWeight: 400 }}> · </span>
                  <span style={{ color: '#6B7A3E', fontWeight: 600 }}>● </span>
                  <span style={{ color: '#6B7A3E', fontWeight: 600 }}>{counts.low} low</span>
                </>}
              </div>
            ) : (
              <div style={{ fontFamily: T.sans, fontSize: 14, color: T.inkMuted }}>
                No findings yet.
              </div>
            )}
          </div>
          {onScan && (
            <Button onClick={onScan} disabled={scanning} variant="primary" size="sm">
              {scanning ? 'Scanning…' : 'Scan now'}
            </Button>
          )}
        </div>

        {hasFindings && (
          <div style={{ display: 'flex', gap: 8, marginTop: 14, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{
              fontFamily: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
              fontSize: 10, fontWeight: 800, letterSpacing: '0.6px',
              textTransform: 'uppercase', color: T.inkMuted, marginRight: 4,
            }}>
              Filter
            </span>
            <FilterTab value="all" label="All" count={counts.all} />
            <FilterTab value="code_ahead" label="→ Code ahead" count={counts.code_ahead} />
            <FilterTab value="spec_ahead" label="← Spec ahead" count={counts.spec_ahead} />
            {repoOptions.length >= 2 && (
              <select
                value={repoFilter}
                onChange={e => setRepoFilter(e.target.value)}
                style={{ padding: '4px 8px', borderRadius: 4 }}
              >
                <option value="">All repos</option>
                {repoOptions.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            )}
          </div>
        )}
      </div>

      {!hasFindings ? (
        <div style={{ padding: '8px 16px 24px 16px', textAlign: 'center' }}>
          <Caption>Run a scan to detect drift between your spec and code.</Caption>
        </div>
      ) : visible.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center' }}>
          <Text>No findings match this filter.</Text>
        </div>
      ) : (
        visible.map(f => (
          <DriftFindingRow
            key={f.id}
            finding={f}
            showRepoBadge={showRepoBadge}
            scannedAt={repoMeta?.[f.repo]?.last_scanned_at}
            onUpdateSpec={onUpdateSpec}
            updateSpecRunning={updateSpecRunning}
            onDismiss={onDismiss}
            dismissRunning={dismissRunning}
          />
        ))
      )}

      {resolved.length > 0 && (
        <div style={{ borderTop: '1px solid var(--color-border)' }}>
          <div
            onClick={() => setResolvedExpanded(!resolvedExpanded)}
            style={{ padding: 12, cursor: 'pointer' }}
          >
            <Text>{resolvedExpanded ? '▼' : '▶'} Resolved ({resolved.length})</Text>
          </div>
          {resolvedExpanded && resolved.map((f, i) => (
            <div key={`r-${i}`} style={{ padding: '8px 16px', opacity: 0.6 }}>
              <Caption>{(f.severity || 'low').toUpperCase()} · {f.title}</Caption>
            </div>
          ))}
        </div>
      )}

      {dismissed.length > 0 && (
        <div style={{ borderTop: '1px solid var(--color-border)' }}>
          <div
            onClick={() => setDismissedExpanded(!dismissedExpanded)}
            style={{ padding: 12, cursor: 'pointer' }}
          >
            <Text>{dismissedExpanded ? '▼' : '▶'} Dismissed ({dismissed.length})</Text>
          </div>
          {dismissedExpanded && dismissed.map((f) => {
            const sig = `${f.repo || ''}::${f.file_path || ''}::${(f.title || '').toLowerCase().split(/\s+/).join(' ').trim()}`;
            return (
              <div
                key={sig}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 16px', opacity: 0.7,
                  borderTop: '1px solid var(--color-border)',
                }}
              >
                <Caption style={{ flex: 1 }}>
                  {(f.severity || 'low').toUpperCase()} · {f.title}
                  {f.repo ? ` · ${f.repo}` : ''}
                  {f.file_path ? ` · ${f.file_path}` : ''}
                </Caption>
                {onUndismiss && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onUndismiss(sig)}
                    disabled={dismissRunning}
                  >
                    Restore
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
